import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { featuredMovies, categoryMap } from '@/lib/catalog';

export default function HeroCarousel() {
  const slides = featuredMovies();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (paused) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [paused, slides.length]);

  const goTo = (next: number) => {
    setIndex((next + slides.length) % slides.length);
  };

  const current = slides[index];
  const category = categoryMap[current.category];

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      aria-label="焦点影视推荐"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(event) => {
        touchStartX.current = event.changedTouches[0].clientX;
      }}
      onTouchEnd={(event) => {
        if (touchStartX.current === null) return;
        const delta = event.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 50) {
          goTo(delta > 0 ? index - 1 : index + 1);
        }
        touchStartX.current = null;
      }}
    >
      <div className="relative h-[500px] w-full sm:h-[540px] lg:h-[600px]">
        {slides.map((movie, slideIndex) => (
          <div
            key={movie.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              slideIndex === index ? 'opacity-100' : 'pointer-events-none opacity-0'
            }`}
            aria-hidden={slideIndex !== index}
          >
            <img
              src={movie.backdrop || movie.poster}
              alt={`${movie.title} 画面`}
              title={`${movie.title} - 焦点推荐 - 黄牛视频`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/75 to-primary-950/25" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/10 to-primary-950/40" />
          </div>
        ))}

        <div className="relative z-10 mx-auto flex h-full w-full max-w-[1600px] items-end px-4 pb-20 sm:px-6 md:items-center md:pb-0 lg:px-10">
          <div className="w-full max-w-2xl">
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex items-center gap-1.5 rounded-full bg-accent-500 px-2.5 py-1 font-label text-[10px] font-bold uppercase tracking-wider text-accent-950">
                <i className="ri-fire-fill text-[10px]" />
                本周焦点
              </span>
              <span className="rounded-full border border-background-50/25 px-2.5 py-1 text-[10px] text-background-100">
                {category.label}
              </span>
              <span className="rounded-full border border-background-50/25 px-2.5 py-1 text-[10px] text-background-100">
                {current.region}
              </span>
            </div>

            <h1
              key={current.id}
              className="mt-4 animate-fade-up font-heading text-3xl font-extrabold leading-tight tracking-tight text-background-50 sm:text-4xl lg:text-6xl"
            >
              {current.title}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-background-100/85 sm:text-sm">
              <span className="flex items-center gap-1 font-label font-bold text-accent-400">
                <i className="ri-star-fill text-xs" />
                {current.rating.toFixed(1)}
              </span>
              <span className="text-background-50/40">|</span>
              <span>{current.year}</span>
              <span className="text-background-50/40">|</span>
              <span>{current.genres.join(' / ')}</span>
              <span className="text-background-50/40">|</span>
              <span>{current.duration}</span>
            </div>

            <p className="mt-3 max-w-xl text-sm font-medium text-background-50/95">
              {current.tagline}
            </p>
            <p className="mt-2 line-clamp-2 max-w-xl text-xs leading-relaxed text-background-100/70 sm:text-sm">
              {current.synopsis}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-accent-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-play-circle-fill text-base" />
                查看详情
              </Link>
              <Link
                to={`/detail/${current.id}#player`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 bg-background-50/10 px-5 py-2.5 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
              >
                <i className="ri-movie-2-line text-base" />
                播放演示
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-4 z-20 flex items-center gap-2 sm:left-6 lg:left-10">
          {slides.map((movie, slideIndex) => (
            <button
              key={movie.id}
              type="button"
              aria-label={`切换到 ${movie.title}`}
              onClick={() => goTo(slideIndex)}
              className={`h-1 cursor-pointer rounded-full transition-all ${
                slideIndex === index
                  ? 'w-8 bg-accent-500'
                  : 'w-4 bg-background-50/35 hover:bg-background-50/60'
              }`}
            />
          ))}
          <span className="ml-2 font-label text-[11px] text-background-50/70">
            {String(index + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
          </span>
        </div>

        <div className="absolute bottom-5 right-4 z-20 hidden items-center gap-2 sm:right-6 md:flex lg:right-10">
          <button
            type="button"
            aria-label="上一部"
            onClick={() => goTo(index - 1)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/15"
          >
            <i className="ri-arrow-left-line text-base" />
          </button>
          <button
            type="button"
            aria-label="下一部"
            onClick={() => goTo(index + 1)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/15"
          >
            <i className="ri-arrow-right-line text-base" />
          </button>
        </div>
      </div>
    </section>
  );
}